import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { logout, resetState } from '../GlobalAction';
import { ActivityTypeEnum, AdvanceActivityStepType } from '../../types/activity';
import { ICoach, ISubscriptionPlanStatus, PaymentStatus } from '../coach/coachSlice';
import { ClientType } from '../../types/client';

export interface IClientObjective {
  id?: string;
  objectiveName: string;
  colorCode: string;
}

export interface IClientProgressData {
  objectiveId: string;
  objectiveName: string;
  colorCode?: string;
  progressList: {
    createdAt: string;
    scale: number;
  }[];
}
export interface IClient {
  id: string;
  firstName: string;
  lastName: string;
  description: string;
  profilePic: string;
  email: string;
  surname: string;
  objectives: IClientObjective[] | null;
  teamMembers?: ICoach[];
  // in the future  this will be a added
  lastActivity?: string;
  engagementLevel?: { id: number; data: number }[];
  tp: string;
  dob: string;
  pronoun: string;
  gender: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  isActive: boolean;
  updatedAt: string;
  archived: string;
  clientType?: ClientType;
  totalUnreadMessages: number | null;
  coach?: ICoach;
}

export interface ICreateClientInput {
  CreateClient: {
    coachId: string;
    clientGroupId?: string;
    coaches?: string[];
    client: {
      firstName: string;
      lastName: string | null;
      surname: string;
      email: string;
      profilePic: string;
      dob?: string | null;
      gender: string;
      tp?: string;
      addressLine1: string;
      addressLine2: string;
      city: string;
      state: string;
      pronoun: string | null;
      organizationId: string;
      archived: boolean;
      objectives: IClientObjective[] | null;
    };
  };
}

export interface IClientInput {
  firstName: string;
  lastName?: string | null;
  description?: string | null;
  surname: string;
  email: string;
  profilePic: string;
  dob?: string | null;
  gender: string;
  tp?: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  pronoun?: string | null;
  organizationId: string;
  archived: boolean;
  objectives?: IClientObjective[] | null;
}

export interface ICreateClientGroupInput {
  client: IClientInput;
  coaches: string[];
  principalCoach: string;
  coachGroupId: string;
  clients?: IClientInput[] | [];
  clientType?: ClientType;
  clientGroupId?: string;
}

export interface IUpdateClientObjectivesInput {
  UpdateClientObjectives: {
    coachId: string;
    clientId: string;
    objectives: IClientObjective[] | null;
    firstName?: string;
    lastName?: string;
    description?: string;
    coaches?: string[];
    coachGroupId?: string;
  };
}

export interface IUpdateClientCoachAssignmentInput {
  clientId: string;
  coachIds: string[];
}

// client interface for UI
export interface ITransformedClient {
  id: string;
  firstName: string;
  lastName: string;
  profilePic: string;
  email: string;
  surname: string;
  objectives: IClientObjective[];
  lastActivity: string;
  engagementLevel: { id: number; data: number }[];
  // extra fields for UI
  name: string;
  objectivesString: string;
  tp?: string;
  dob?: string;
  pronoun?: string;
  gender?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
}
export interface IClients {
  clients: ITransformedClient[];
}

export interface ICompletedActivity {
  firstName: string;
  lastName: string;
  clientId: string;
  clientProfilePic: string;
  coachId: string;
  activityName: string;
  completedDate: string;
  isNew?: boolean;
}

export interface INewCompletedActivity {
  newActivityCompleted: ICompletedActivity;
}

export interface IClientCompletedActivityWithTotalRecordCount {
  completedActivities: ICompletedActivity[];
  totalCompletedActivityCount: number;
}

export interface IActivityStepResponse {
  id: string;
  activityStepId: string;
  asset: string;
  assetType: string;
  assetDuration: number;
}

export interface IActivityStep {
  id: string;
  description: string;
  stepOrder: number;
  text: string;
  asset: string;
  assetType: string;
  advanceStepType: AdvanceActivityStepType;
  isAdvanceStep: boolean;
  ActivityStepResponse: IActivityStepResponse[];
}

export interface EngagementLevel {
  date: string;
  engagementLevel: 'NONE' | 'PARTIAL' | 'FULL' | 'NO_DATA';
}

export interface IUserActivityProgress {
  engagementLevels: EngagementLevel[];
  suggestions: {
    progress: string[] | null;
  };
  objectiveProgressData: IClientProgressData[];
}

export interface ICompletedActivities {
  today: {
    activity: ICompletedActivity[];
    totalCompletedActivityCount: number;
  };
  yesterday: {
    activity: ICompletedActivity[];
    totalCompletedActivityCount: number;
  };
  last7Days: {
    activity: ICompletedActivity[];
    totalCompletedActivityCount: number;
  };
  earlier: {
    activity: ICompletedActivity[];
    from: string;
    to: string;
  };
  all: ICompletedActivity[];
}

interface IActivityStatus {
  name: string;
  activities: string;
}

export interface IClientActivityStatus {
  completedStatus: IActivityStatus[];
  inCompleteActivities: IActivityStatus[];
}

export interface IResponseSummaryData {
  createdAt: string;
  value: string;
  type: string;
  duration: number;
}

export interface IResponseSummary {
  advanceStepData: string[];
  advanceStepType: AdvanceActivityStepType;
  data: IResponseSummaryData[];
  text: string;
}

export interface IActivityDetails {
  id: string;
  isAdvanceActivity: boolean;
  completedAt: string;
  ActivityStep: IActivityStep[];
}

export interface IActivitySummary {
  name: string;
  schedule: ActivityTypeEnum | null;
  groupId: string;
  responseSummary: IResponseSummary[];
  activityDetails: IActivityDetails[];
}

export interface IActivityPogressSummaryPayload {
  count: number;
  activitySummary: IActivitySummary[];
}

export interface ICreatePrivacySettingsPayload {
  clientId: string;
  coachId: string;
  noteAssistEnabled: boolean;
  magicSuggestionsEnabled: boolean;
}

export interface IUpdatePrivacySettingsPayload {
  concentId: string;
  clientId: string;
  coachId: string;
  noteAssistEnabled: boolean;
  magicSuggestionsEnabled: boolean;
}

export interface IPrivacySettings {
  id: string;
  noteAssistEnabled: boolean;
  magicSuggestionsEnabled: boolean;
}

export enum ProgressBlocks {
  PROGRESS = 'progress',
  ACTIVITY = 'activity',
}

export interface IClientState {
  totalClientCount: number;
  clients: IClient[];
  totalArchivedClientCount: number;
  archivedClients: IClient[];
  selectedClient: IClient | null;
  selectedClientObjectivesData: IClientProgressData[];
  loading: boolean;
  error: string | null;
  clientCompletedActivities: ICompletedActivities;
  clientProgress: IUserActivityProgress;
  fetchingCompletedActivities: boolean;
  fetchedActivityPaginationData: {
    to: String;
    from: String;
  };
  noMoreData: boolean;
  archiveClientLoading: boolean;
  archiveClientSuccess: boolean;
  clientsActivityStatus: IClientActivityStatus;
  generateClientActivitySummeryLoading: boolean;
  fetchClientsLoading: boolean;
  fetchArchivedClientsLoading: boolean;
  activityProgressSummary: {
    count: number;
    activitySummary: IActivitySummary[];
  };
  block: ProgressBlocks;
  activity: IActivitySummary;
  clientActivityProgressLoading: boolean;
  clientActivityProgressSummaryLoading: boolean;
  changeClientStatusLoading: boolean;
  changeClientStatusError: string;
  statusChangedClientId: string;
  isAddClientSuccess: boolean;
  addClientLoading: boolean;
  fetchPrivacySettingsLoading: boolean;
  updatePrivacySettingsLoading: boolean;
  createPrivacySettingsLoading: boolean;
  privacySettings: IPrivacySettings;
  openPrivacySettingsConcentModal: boolean;
  openUpdatePrivacySettingsConcentModal: boolean;
  paymentStatus: PaymentStatus | null;
  subscriptionPlanStatus?: ISubscriptionPlanStatus;
}

export interface IFetchClientPayload {
  coachId: string;
  nameText?: string;
  skip?: number;
  take?: number;
  isArchived: boolean;
  orderBy?: string;
  order?: string;
  isOnlyClientName?: boolean;
  clientType?: ClientType;
  clientGroupId?: string;
}

export interface IGetClientByIdPayload {
  selectedClientId: string;
}

export interface IArchiveClientPayload {
  coachId: string;
  clientId: string;
  email: string;
  password?: string;
  clientGroupId?: string;
}

export interface IFetchObjectivesDataPayload {
  fromDate: string;
  toDate: string;
  clientId: string;
}

export interface IGetCompletedActivityPayload {
  coachId: string;
  from: string;
  to: string;
  skip: Number;
  take: Number;
}

export interface IGetUserActivityProgressPayload {
  coachId: string;
  clientId: string;
  skip?: number;
  take?: number;
}

export enum ClientStatus {
  ENABLE = 'ENABLE',
  DISABLE = 'DISABLE',
}
export interface IChangeClientStatusPayload {
  clientId: string;
  status: ClientStatus;
  coachId: string;
  clientGroupId?: string;
}

const initialState: IClientState = {
  totalClientCount: 0,
  clients: [],
  totalArchivedClientCount: 0,
  archivedClients: [],
  selectedClient: null,
  selectedClientObjectivesData: [],
  clientProgress: {
    engagementLevels: [],
    suggestions: {
      progress: null,
    },
    objectiveProgressData: [],
  },
  loading: false,
  error: null,
  clientCompletedActivities: {
    today: {
      activity: [],
      totalCompletedActivityCount: 0,
    },
    yesterday: {
      activity: [],
      totalCompletedActivityCount: 0,
    },
    last7Days: {
      activity: [],
      totalCompletedActivityCount: 0,
    },
    all: [],
    earlier: {
      activity: [],
      from: '',
      to: '',
    },
  },
  fetchingCompletedActivities: false,
  fetchedActivityPaginationData: {
    to: '',
    from: '',
  },
  noMoreData: false,
  archiveClientLoading: false,
  clientsActivityStatus: {
    completedStatus: [],
    inCompleteActivities: [],
  },
  generateClientActivitySummeryLoading: false,
  fetchClientsLoading: true,
  activityProgressSummary: {
    count: 0,
    activitySummary: [],
  },
  block: ProgressBlocks.PROGRESS,
  activity: {
    name: '',
    schedule: null,
    groupId: '',
    responseSummary: [],
    activityDetails: [],
  },
  clientActivityProgressLoading: false,
  clientActivityProgressSummaryLoading: false,
  changeClientStatusLoading: false,
  changeClientStatusError: '',
  statusChangedClientId: '',
  fetchArchivedClientsLoading: false,
  archiveClientSuccess: false,
  isAddClientSuccess: false,
  addClientLoading: false,
  fetchPrivacySettingsLoading: false,
  privacySettings: {
    id: '',
    noteAssistEnabled: false,
    magicSuggestionsEnabled: false,
  },
  updatePrivacySettingsLoading: false,
  createPrivacySettingsLoading: false,
  openPrivacySettingsConcentModal: false,
  openUpdatePrivacySettingsConcentModal: false,
  paymentStatus: null,
};

const clientSlice = createSlice({
  name: 'clients',
  initialState,
  reducers: {
    fetchClientsRequest: (state: IClientState, _action: PayloadAction<IFetchClientPayload>) => {
      if (state.selectedClient?.clientType !== ClientType.GROUP) {
        state.selectedClient = null;
      }
      state.fetchClientsLoading = true;
    },
    fetchClientsSuccess: (state: IClientState, action) => {
      state.fetchClientsLoading = false;
      state.clients = action.payload.clients;
      state.totalClientCount = action.payload.totalClientCount;
      state.paymentStatus = action.payload.paymentStatus;
      state.subscriptionPlanStatus = action.payload.subscriptionPlanStatus;
    },
    fetchClientsFailure: (state: IClientState, action) => {
      state.fetchClientsLoading = false;
      state.error = action.payload;
    },
    fetchSelectedClientRequest: (
      state: IClientState,
      _action: PayloadAction<IGetClientByIdPayload>
    ) => {
      state.loading = true;
    },
    fetchSelectedClientSuccess: (state: IClientState, action) => {
      state.loading = false;
      state.selectedClient = action.payload;
    },
    fetchSelectedClientFailure: (state: IClientState, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    addClientRequest: (state: IClientState, _action: PayloadAction<ICreateClientInput>) => {
      state.addClientLoading = true;
    },
    addClientGroupRequest: (
      state: IClientState,
      _action: PayloadAction<ICreateClientGroupInput>
    ) => {
      state.addClientLoading = true;
    },
    addClientSuccess: (state: IClientState, _action: PayloadAction<IClient>) => {
      state.addClientLoading = false;
      state.isAddClientSuccess = true;
    },
    closeAddClientOnSuccess: (state: IClientState) => {
      state.isAddClientSuccess = false;
    },
    addClientFailure: (state: IClientState) => {
      state.addClientLoading = false;
    },
    archiveClientRequest: (state: IClientState, _action: PayloadAction<IArchiveClientPayload>) => {
      state.archiveClientLoading = true;
    },
    archiveClientSuccess: (state: IClientState, action) => {
      state.archiveClientLoading = false;
      state.archiveClientSuccess = true;
      state.clients = state.clients.filter((client) => client.id !== action.payload.id);
      state.totalClientCount -= 1;
    },
    closeArchieveModal: (state: IClientState) => {
      state.archiveClientSuccess = false;
    },
    archiveClientFailure: (state: IClientState, action) => {
      state.archiveClientLoading = false;
      state.error = action.payload;
    },
    getCompletedActivitiesRequest: (
      state: IClientState,
      _action: PayloadAction<IGetCompletedActivityPayload>
    ) => {
      state.fetchingCompletedActivities = true;
    },
    getCompletedActivitiesSuccess: (
      state: IClientState,
      action: PayloadAction<ICompletedActivities>
    ) => {
      state.fetchingCompletedActivities = false;
      state.clientCompletedActivities = action.payload;
    },
    getCompletedActivitiesFailure: (state: IClientState, action) => {
      state.fetchingCompletedActivities = false;
      state.error = action.payload;
    },
    setFetchedActivityPaginationData: (
      state: IClientState,
      action: PayloadAction<{ to: String; from: String }>
    ) => {
      state.fetchedActivityPaginationData = action.payload;
    },
    addNewActivityToToday: (state: IClientState, action: PayloadAction<INewCompletedActivity>) => {
      state.clientCompletedActivities.today.activity = [
        ...state.clientCompletedActivities.today.activity,
        action.payload.newActivityCompleted,
      ];

      state.clientCompletedActivities.today.totalCompletedActivityCount += 1;
      state.clientCompletedActivities.all.push({
        ...action.payload.newActivityCompleted,
      });
    },

    getEarlierActivitiesRequest: (
      state: IClientState,
      _action: PayloadAction<IGetCompletedActivityPayload>
    ) => {
      state.fetchingCompletedActivities = true;
    },
    getEarlierActivitiesSuccess: (
      state: IClientState,
      action: PayloadAction<ICompletedActivities>
    ) => {
      state.fetchingCompletedActivities = false;
      if (action.payload.earlier.activity.length === 0) {
        state.noMoreData = true;
      } else {
        state.clientCompletedActivities.earlier = action.payload.earlier;
      }
    },
    getEarlierActivitiesFailure: (state: IClientState, action) => {
      state.fetchingCompletedActivities = false;
      state.error = action.payload;
    },
    getSelectedClientObjectivesDataRequest: (
      state: IClientState,
      _action: PayloadAction<IFetchObjectivesDataPayload>
    ) => {
      state.loading = true;
    },
    getSelectedClientObjectivesDataSuccess: (
      state: IClientState,
      action: PayloadAction<IClientProgressData[]>
    ) => {
      state.loading = false;
      state.selectedClientObjectivesData = action.payload;
    },
    getSelectedClientObjectivesDataFailure: (state: IClientState, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    getUserActivityProgressRequest: (
      state: IClientState,
      _action: PayloadAction<IGetUserActivityProgressPayload>
    ) => {
      state.clientActivityProgressLoading = true;
    },
    getUserActivityProgressSuccess: (state: IClientState, action) => {
      state.clientActivityProgressLoading = false;
      state.clientProgress = action.payload;
    },
    clearUserActivityProgress: (state: IClientState) => {
      state.clientProgress.objectiveProgressData = [];
      state.clientProgress.engagementLevels = [];
      state.clientProgress.suggestions.progress = [];
    },
    getUserActivityProgressFailure: (state: IClientState, action) => {
      state.clientActivityProgressLoading = false;
      state.error = action.payload;
    },
    generateClientActivityStatusSummeryRequest: (
      state: IClientState,
      _action: PayloadAction<{ coachId: string; timeZone: string }>
    ) => {
      state.generateClientActivitySummeryLoading = true;
    },
    generateClientActivityStatusSummerySuccess: (state: IClientState) => {
      state.generateClientActivitySummeryLoading = false;
    },
    generateClientActivityStatusSummeryFailure: (state: IClientState) => {
      state.generateClientActivitySummeryLoading = false;
    },
    getClientActivityProgressSummaryRequest: (
      state: IClientState,
      _action: PayloadAction<IGetUserActivityProgressPayload>
    ) => {
      state.clientActivityProgressSummaryLoading = true;
    },
    getClientActivityProgressSummarySuccess: (
      state: IClientState,
      action: PayloadAction<IActivityPogressSummaryPayload>
    ) => {
      state.clientActivityProgressSummaryLoading = false;
      state.activityProgressSummary.count = action.payload.count;
      state.activityProgressSummary.activitySummary = [
        ...state.activityProgressSummary.activitySummary,
        ...action.payload.activitySummary,
      ];
    },
    clearActivityProgressSummary: (state: IClientState) => {
      state.activityProgressSummary.activitySummary = [];
      state.activityProgressSummary.count = 0;
    },
    getClientActivityProgressSummaryFailure: (state: IClientState, action) => {
      state.clientActivityProgressSummaryLoading = false;
      state.error = action.payload;
    },
    setProgressSingleActivityBlock: (state: IClientState, action) => {
      state.block = ProgressBlocks.ACTIVITY;
      state.activity = action.payload;
    },
    setClientProgressBlock: (state: IClientState) => {
      state.block = ProgressBlocks.PROGRESS;
    },
    changeClientStatus: (state, action: PayloadAction<IChangeClientStatusPayload>) => {
      state.changeClientStatusLoading = true;
      state.statusChangedClientId = action.payload.clientId;
    },
    changeClientStatusSuccess: (state, action: PayloadAction<boolean>) => {
      const clientToUpdate = state.clients.find((v) => v.id === state.statusChangedClientId);
      if (clientToUpdate) {
        clientToUpdate.isActive = action.payload;
        state.statusChangedClientId = '';
        state.changeClientStatusLoading = false;
      }
    },
    changeClientStatusFailure: (state, action: PayloadAction<string>) => {
      state.changeClientStatusLoading = false;
      state.changeClientStatusError = action.payload;
    },
    fetchArchivedClients: (state: IClientState, _action: PayloadAction<IFetchClientPayload>) => {
      state.fetchArchivedClientsLoading = true;
    },
    fetchArchivedClientsSuccess: (state: IClientState, action) => {
      state.fetchArchivedClientsLoading = false;
      state.archivedClients = action.payload.clients;
      state.totalArchivedClientCount = action.payload.totalClientCount;
      state.paymentStatus = action.payload.paymentStatus;
    },
    fetchArchivedClientsFailure: (state: IClientState, action) => {
      state.fetchArchivedClientsLoading = false;
      state.error = action.payload;
    },
    fetchClientPrivacySettings: (
      state: IClientState,
      _action: PayloadAction<{ clientId: string; coachId: string }>
    ) => {
      state.fetchPrivacySettingsLoading = true;
      state.privacySettings = {
        id: '',
        noteAssistEnabled: false,
        magicSuggestionsEnabled: false,
      };
    },
    fetchClientPrivacySettingsSuccess: (state: IClientState, action) => {
      state.fetchPrivacySettingsLoading = false;
      state.privacySettings = action.payload;
    },
    fetchClientPrivacySettingsFailure: (state: IClientState, action) => {
      state.fetchPrivacySettingsLoading = false;
      state.error = action.payload;
    },
    createClientPrivacySettings: (
      state: IClientState,
      _action: PayloadAction<ICreatePrivacySettingsPayload>
    ) => {
      state.createPrivacySettingsLoading = true;
    },
    createClientPrivacySettingsSuccess: (state: IClientState, action) => {
      state.createPrivacySettingsLoading = false;
      state.openPrivacySettingsConcentModal = false;
      state.privacySettings = action.payload;
    },
    createClientPrivacySettingsFailure: (state: IClientState, action) => {
      state.createPrivacySettingsLoading = false;
      state.error = action.payload;
    },
    updateClientPrivacySettings: (
      state: IClientState,
      _action: PayloadAction<IUpdatePrivacySettingsPayload>
    ) => {
      state.updatePrivacySettingsLoading = true;
    },
    updateClientPrivacySettingsSuccess: (state: IClientState, action) => {
      state.updatePrivacySettingsLoading = false;
      state.openUpdatePrivacySettingsConcentModal = false;
      state.privacySettings = action.payload;
    },
    updateClientPrivacySettingsFailure: (state: IClientState, action) => {
      state.updatePrivacySettingsLoading = false;
      state.error = action.payload;
    },
    setOpenPrivacySettingsConcentModal: (state: IClientState, action: PayloadAction<boolean>) => {
      state.openPrivacySettingsConcentModal = action.payload;
    },
    setOpenUpdatePrivacySettingsConcentModal: (
      state: IClientState,
      action: PayloadAction<boolean>
    ) => {
      state.openUpdatePrivacySettingsConcentModal = action.payload;
    },
    resetClientsTableData: (state: IClientState) => {
      state.error = null;
      state.fetchClientsLoading = false;
      state.totalClientCount = 0;
      state.fetchArchivedClientsLoading = false;
      state.totalArchivedClientCount = 0;
    },
    resendClientInvitation: (
      _state: IClientState,
      _action: PayloadAction<{ clientId: string }>
    ) => {},
  },
  extraReducers: (builder) => {
    builder.addCase(resetState, (state) => {
      state.selectedClient = null;
      state.loading = false;
      state.error = null;
      state.clientProgress = {
        engagementLevels: [],
        suggestions: {
          progress: [],
        },
        objectiveProgressData: [],
      };
      state.activityProgressSummary = {
        count: 0,
        activitySummary: [],
      };
      state.activity = {
        name: '',
        schedule: null,
        groupId: '',
        responseSummary: [],
        activityDetails: [],
      };
      state.block = ProgressBlocks.PROGRESS;
    });
    builder.addCase(logout, (state) => {
      state.selectedClient = null;
      state.loading = false;
      state.error = null;
      state.clientProgress = {
        engagementLevels: [],
        suggestions: {
          progress: [],
        },
        objectiveProgressData: [],
      };
      state.activityProgressSummary = {
        count: 0,
        activitySummary: [],
      };
      state.activity = {
        name: '',
        schedule: null,
        groupId: '',
        responseSummary: [],
        activityDetails: [],
      };
      state.block = ProgressBlocks.PROGRESS;
    });
  },
});

export const {
  fetchClientsRequest,
  fetchClientsSuccess,
  fetchClientsFailure,
  fetchSelectedClientRequest,
  fetchSelectedClientSuccess,
  fetchSelectedClientFailure,
  addClientRequest,
  addClientGroupRequest,
  addClientSuccess,
  archiveClientRequest,
  archiveClientSuccess,
  archiveClientFailure,
  getCompletedActivitiesRequest,
  getCompletedActivitiesSuccess,
  getCompletedActivitiesFailure,
  setFetchedActivityPaginationData,
  addNewActivityToToday,
  getEarlierActivitiesRequest,
  getEarlierActivitiesSuccess,
  getEarlierActivitiesFailure,
  getSelectedClientObjectivesDataRequest,
  getSelectedClientObjectivesDataSuccess,
  getSelectedClientObjectivesDataFailure,
  getUserActivityProgressRequest,
  getUserActivityProgressSuccess,
  getUserActivityProgressFailure,
  generateClientActivityStatusSummeryRequest,
  generateClientActivityStatusSummerySuccess,
  generateClientActivityStatusSummeryFailure,
  getClientActivityProgressSummaryRequest,
  getClientActivityProgressSummarySuccess,
  getClientActivityProgressSummaryFailure,
  setProgressSingleActivityBlock,
  setClientProgressBlock,
  clearActivityProgressSummary,
  clearUserActivityProgress,
  changeClientStatus,
  changeClientStatusSuccess,
  changeClientStatusFailure,
  fetchArchivedClients,
  fetchArchivedClientsSuccess,
  fetchArchivedClientsFailure,
  closeArchieveModal,
  closeAddClientOnSuccess,
  addClientFailure,
  fetchClientPrivacySettings,
  fetchClientPrivacySettingsSuccess,
  fetchClientPrivacySettingsFailure,
  createClientPrivacySettings,
  createClientPrivacySettingsSuccess,
  createClientPrivacySettingsFailure,
  updateClientPrivacySettings,
  updateClientPrivacySettingsSuccess,
  updateClientPrivacySettingsFailure,
  setOpenPrivacySettingsConcentModal,
  setOpenUpdatePrivacySettingsConcentModal,
  resetClientsTableData,
  resendClientInvitation,
} = clientSlice.actions;

export default clientSlice.reducer;
